import LogoSvg from '@/assets/wntd-wordmark-orange.svg';

interface OutfitItem {
  name: string;
  imageUrl?: string;
  category: string;
}

interface OutfitChallengeShareCardProps {
  theme: string;
  score: number;
  starRating: number;
  difficulty?: string;
  stylistSummary?: string;
  items?: OutfitItem[];
  streak?: number;
  level?: number;
}

const DIFFICULTY_COLOR: Record<string, string> = {
  easy:   'rgba(34,197,94,0.18)',
  medium: 'rgba(255,111,59,0.18)',
  hard:   'rgba(239,68,68,0.18)',
};
const DIFFICULTY_TEXT: Record<string, string> = {
  easy:   '#22C55E',
  medium: '#FF6F3B',
  hard:   '#EF4444',
};

function scoreToColor(score: number): string {
  if (score >= 80) return '#22C55E';
  if (score >= 60) return '#FF6F3B';
  return '#EF4444';
}

function scoreToLabel(score: number): string {
  if (score >= 90) return 'Outstanding';
  if (score >= 80) return 'Excellent';
  if (score >= 70) return 'Great Look';
  if (score >= 60) return 'Solid Effort';
  return 'Keep Trying';
}

export function OutfitChallengeShareCard({
  theme,
  score,
  starRating,
  difficulty,
  stylistSummary,
  items = [],
  streak,
  level,
}: OutfitChallengeShareCardProps) {
  const displayItems  = items.slice(0, 5);
  const itemsWithImages = displayItems.filter(i => i.imageUrl);
  const fullStars    = Math.floor(starRating);
  const hasHalfStar  = (starRating % 1) >= 0.5;
  const totalStars   = 5;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a',
        padding: '48px 48px 40px',
      }}
    >
      {/* Header — theme + difficulty */}
      <div style={{ flexShrink: 0, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <p
            style={{
              fontSize: 20,
              color: 'rgba(255,255,255,0.4)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 2,
            }}
          >
            Daily Outfit Challenge
          </p>
          {difficulty && (
            <span
              style={{
                fontSize: 18,
                padding: '4px 16px',
                borderRadius: 20,
                backgroundColor: DIFFICULTY_COLOR[difficulty] || 'rgba(255,255,255,0.1)',
                color: DIFFICULTY_TEXT[difficulty] || 'rgba(255,255,255,0.6)',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              {difficulty}
            </span>
          )}
        </div>
        <p
          style={{
            fontSize: 46,
            fontWeight: 800,
            color: 'white',
            lineHeight: 1.1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {theme}
        </p>
      </div>

      {/* Score hero */}
      <div
        style={{
          flexShrink: 0,
          padding: '36px 40px',
          borderRadius: 32,
          backgroundColor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.07)',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 40,
        }}
      >
        {/* Score number */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <span
            style={{
              fontSize: 92,
              fontWeight: 900,
              color: scoreToColor(score),
              lineHeight: 1,
              display: 'block',
            }}
          >
            {score}
          </span>
          <span
            style={{
              fontSize: 24,
              color: 'rgba(255,255,255,0.4)',
              fontWeight: 600,
            }}
          >
            / 100
          </span>
        </div>

        <div style={{ flex: 1 }}>
          {/* Star rating */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {Array.from({ length: totalStars }).map((_, i) => (
              <span
                key={i}
                style={{
                  fontSize: 34,
                  color: i < fullStars || (i === fullStars && hasHalfStar)
                    ? '#FBBF24'
                    : 'rgba(255,255,255,0.12)',
                }}
              >
                ★
              </span>
            ))}
          </div>

          {/* Label */}
          <p
            style={{
              fontSize: 30,
              fontWeight: 700,
              color: 'white',
              marginBottom: 12,
            }}
          >
            {scoreToLabel(score)}
          </p>

          {/* Streak + level badges */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {streak !== undefined && streak > 0 && (
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.6)',
                  padding: '6px 16px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.09)',
                }}
              >
                🔥 {streak}-day streak
              </span>
            )}
            {level !== undefined && (
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.6)',
                  padding: '6px 16px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.09)',
                }}
              >
                ⭐ Level {level}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Outfit items strip — only if images available */}
      {itemsWithImages.length > 0 && (
        <div style={{ flexShrink: 0, marginBottom: 20 }}>
          <p
            style={{
              fontSize: 18,
              color: 'rgba(255,255,255,0.35)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 2,
              marginBottom: 14,
            }}
          >
            My Outfit
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            {itemsWithImages.map((item, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  aspectRatio: '3/4',
                  borderRadius: 20,
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,0.06)',
                  position: 'relative',
                }}
              >
                <img
                  src={item.imageUrl!}
                  alt={item.name}
                  crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '20px 8px 8px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)',
                  }}
                >
                  <p
                    style={{
                      fontSize: 16,
                      color: 'rgba(255,255,255,0.6)',
                      fontWeight: 500,
                      textAlign: 'center',
                      textTransform: 'capitalize',
                    }}
                  >
                    {item.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stylist summary */}
      {stylistSummary && (
        <div
          style={{
            flex: 1,
            padding: '24px 28px',
            borderRadius: 24,
            backgroundColor: 'rgba(255,111,59,0.07)',
            border: '1px solid rgba(255,111,59,0.14)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 28, flexShrink: 0 }}>💬</span>
          <p
            style={{
              fontSize: 22,
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.55,
              fontStyle: 'italic',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            "{stylistSummary}"
          </p>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 36,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={LogoSvg} alt="WNTD" style={{ height: 36 }} />
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Daily Challenge
          </span>
        </div>
        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.3)' }}>wntd.ai</span>
      </div>
    </div>
  );
}
